import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, CheckCircle2, ChevronRight, GraduationCap, Info, Loader2, Pen, X } from 'lucide-react';
import { toast } from 'sonner';
import { me, training, type EisfTaskRow } from '@/api/client';
import { PageHeader } from '@/components/layout/PageHeader';
import { SignApprovalDialog } from '@/pages/binder/components/SignApprovalDialog';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';

/**
 * "Approval Needed" — the approver's landing page. Filters
 * `/me/tasks?status=open` to `kind === 'approval-needed'`, i.e. tasks
 * spawned by `services/eisf/approvalWorkflows.ts` when a version is
 * routed to this user for signature. Rows deep-link into the binder
 * so the approver can open the doc + hit the OTP → DocuSeal sign flow.
 *
 * Non-approval tasks (expired docs, staff readiness, etc.) still surface
 * via the AppShell TasksBell — this page is scoped to signing work.
 */

interface ApprovalTaskDetails {
  binder_id?: string;
  document_id?: string;
  version_id?: string;
  routing_id?: string;
  workflow_name?: string;
  signature_meaning?: string;
  /** Standalone/external training — kind: 'training-approval-needed' tasks. */
  manual_training_id?: string;
  training_name?: string;
  training_kind?: 'internal' | 'external';
}

function parseDetails(task: EisfTaskRow): ApprovalTaskDetails {
  if (!task.details_json) return {};
  try {
    return JSON.parse(task.details_json) as ApprovalTaskDetails;
  } catch {
    return {};
  }
}

function relativeAge(iso: string): string {
  // Caspio returns datetime strings without a timezone suffix (UTC-naive) —
  // append 'Z' so JS parses them as UTC rather than local time, or every
  // task looks (local UTC offset) hours older than it really is.
  const isoUtc = iso.endsWith('Z') ? iso : `${iso}Z`;
  const then = new Date(isoUtc).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.round((now - then) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  return `${d}d`;
}

interface SignTarget {
  versionId: string;
  versionLabel: string;
  documentTitle: string;
  workflowName: string | null;
}

type ApprovalTab = 'documents' | 'training';

export default function ApprovalNeededPage() {
  const qc = useQueryClient();
  const [signTarget, setSignTarget] = useState<SignTarget | null>(null);
  const [tab, setTab] = useState<ApprovalTab>('documents');

  const tasksQ = useQuery({
    queryKey: ['me', 'tasks', 'open'],
    queryFn: () => me.listTasks('open'),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const approvalTasks = useMemo(
    () => (tasksQ.data?.tasks ?? []).filter((t) => t.kind === 'approval-needed'),
    [tasksQ.data],
  );

  // Standalone/external training — a separate task kind, kept in its own
  // section below rather than merged into the table above: the action
  // affordance genuinely differs (Approve/Reject, no e-signature) from
  // every other eISF approval's Sign now/Review/Dismiss.
  const trainingApprovalTasks = useMemo(
    () => (tasksQ.data?.tasks ?? []).filter((t) => t.kind === 'training-approval-needed'),
    [tasksQ.data],
  );
  const [rejectingTaskId, setRejectingTaskId] = useState<string | null>(null);

  const approveTraining = useMutation({
    mutationFn: (manualTrainingId: string) => training.manual.approve(manualTrainingId),
    onSuccess: (result) => {
      toast.success(
        result.assigned > 0
          ? `Training approved — assigned to ${result.assigned} staff member${result.assigned === 1 ? '' : 's'}.`
          : 'Training approved.',
      );
      qc.invalidateQueries({ queryKey: ['me', 'tasks', 'open'] });
      qc.invalidateQueries({ queryKey: ['training'] });
    },
    onError: (err) => toast.error(errorText(err, 'Failed to approve training')),
  });

  const dismiss = useMutation({
    mutationFn: (taskId: string) => me.resolveTask(taskId, 'dismissed'),
    onSuccess: () => {
      toast.success('Task dismissed.');
      qc.invalidateQueries({ queryKey: ['me', 'tasks', 'open'] });
    },
    onError: (err) => toast.error(errorText(err, 'Failed')),
  });

  const totalPending = approvalTasks.length + trainingApprovalTasks.length;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Approval needed"
        subtitle="Work routed to you for approval — document signatures and standalone training sign-off."
        actions={
          <span className="badge badge-info text-xs">
            {totalPending} pending
          </span>
        }
      />

      {tasksQ.isLoading && (
        <div className="p-8 flex items-center justify-center gap-2 text-sm text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your approval queue…
        </div>
      )}

      {tasksQ.isError && (
        <div className={`p-4 rounded border flex items-start gap-2 text-sm ${errorToneClass(tasksQ.error)}`}>
          {isPermissionError(tasksQ.error) ? <Info className="h-4 w-4 mt-0.5 shrink-0"/> : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />}
          {errorText(tasksQ.error, 'Failed to load tasks.')}
        </div>
      )}

      {!tasksQ.isLoading && !tasksQ.isError && (
        <>
          <div className="border-b border-surface-border">
            <nav className="-mb-px flex gap-1" aria-label="Approval tabs">
              <TabButton
                active={tab === 'documents'}
                onClick={() => setTab('documents')}
                icon={<Pen className="h-4 w-4" />}
                label={`Documents${approvalTasks.length ? ` (${approvalTasks.length})` : ''}`}
              />
              <TabButton
                active={tab === 'training'}
                onClick={() => setTab('training')}
                icon={<GraduationCap className="h-4 w-4" />}
                label={`Training${trainingApprovalTasks.length ? ` (${trainingApprovalTasks.length})` : ''}`}
              />
            </nav>
          </div>

          {tab === 'documents' && approvalTasks.length === 0 && (
            <div className="p-10 rounded border border-surface-border bg-surface-base text-center">
              <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-500 mb-2" />
              <div className="text-sm text-ink-primary font-medium">You&rsquo;re caught up.</div>
              <div className="text-xs text-ink-muted mt-1">
                No documents are waiting on your signature.
              </div>
            </div>
          )}

          {tab === 'training' && trainingApprovalTasks.length === 0 && (
            <div className="p-10 rounded border border-surface-border bg-surface-base text-center">
              <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-500 mb-2" />
              <div className="text-sm text-ink-primary font-medium">You&rsquo;re caught up.</div>
              <div className="text-xs text-ink-muted mt-1">
                No standalone trainings are waiting on your approval.
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'documents' && approvalTasks.length > 0 && (
        <div className="rounded border border-surface-border bg-surface-base overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted/40 text-xs uppercase tracking-wide text-ink-muted">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Document</th>
                <th className="text-left px-4 py-2 font-medium">Workflow</th>
                <th className="text-left px-4 py-2 font-medium">Signature meaning</th>
                <th className="text-left px-4 py-2 font-medium">Age</th>
                <th className="text-right px-4 py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border/40">
              {approvalTasks.map((task) => {
                const d = parseDetails(task);
                const to = d.binder_id ? `/binder/${encodeURIComponent(d.binder_id)}` : '/binder';
                return (
                  <tr key={task.task_id} className="hover:bg-surface-muted/20">
                    <td className="px-4 py-3">
                      <div className="text-ink-primary">{task.message}</div>
                      {d.version_id && (
                        <div className="text-[11px] text-ink-muted mt-0.5">
                          Routing {d.routing_id?.slice(0, 8) ?? '—'}… · Version {d.version_id.slice(0, 8)}…
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-ink-primary">
                      {d.workflow_name ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-ink-primary">
                      {d.signature_meaning ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-ink-muted">
                      {relativeAge(task.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        {d.version_id && (
                          <button
                            type="button"
                            onClick={() =>
                              setSignTarget({
                                versionId: d.version_id!,
                                versionLabel: '',
                                documentTitle: task.message,
                                workflowName: d.workflow_name ?? null,
                              })
                            }
                            className="inline-flex items-center gap-1 rounded bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700"
                            title="Verify identity and sign right here"
                          >
                            <Pen className="h-3 w-3" />
                            Sign now
                          </button>
                        )}
                        <Link
                          to={to}
                          className="inline-flex items-center gap-1 rounded border border-surface-border bg-white px-2.5 py-1 text-xs font-medium text-ink-primary hover:bg-surface-muted/40"
                          title="Open the document to review before signing"
                        >
                          Review
                          <ChevronRight className="h-3 w-3" />
                        </Link>
                        <button
                          type="button"
                          className="text-[11px] text-ink-muted hover:text-ink-primary underline"
                          onClick={() => dismiss.mutate(task.task_id)}
                          disabled={dismiss.isPending}
                          title="Dismiss this task without signing. The routing row stays open — someone else in the pool can still sign."
                        >
                          Dismiss
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {signTarget && (
        <SignApprovalDialog
          versionId={signTarget.versionId}
          versionLabel={signTarget.versionLabel}
          documentTitle={signTarget.documentTitle}
          workflowName={signTarget.workflowName}
          open={!!signTarget}
          onClose={() => setSignTarget(null)}
          onSigned={() => qc.invalidateQueries({ queryKey: ['me', 'tasks', 'open'] })}
        />
      )}

      {tab === 'training' && trainingApprovalTasks.length > 0 && (
        <div className="rounded border border-surface-border bg-surface-base overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted/40 text-xs uppercase tracking-wide text-ink-muted">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Training</th>
                <th className="text-left px-4 py-2 font-medium">Kind</th>
                <th className="text-left px-4 py-2 font-medium">Age</th>
                <th className="text-right px-4 py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border/40">
              {trainingApprovalTasks.map((task) => {
                const d = parseDetails(task);
                return (
                  <tr key={task.task_id} className="hover:bg-surface-muted/20">
                    <td className="px-4 py-3 text-ink-primary">{d.training_name ?? task.message}</td>
                    <td className="px-4 py-3 text-ink-muted capitalize">{d.training_kind ?? '—'}</td>
                    <td className="px-4 py-3 text-ink-muted">{relativeAge(task.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => d.manual_training_id && approveTraining.mutate(d.manual_training_id)}
                          disabled={approveTraining.isPending || !d.manual_training_id}
                          className="inline-flex items-center gap-1 rounded bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Check className="h-3 w-3" /> Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => setRejectingTaskId(task.task_id)}
                          className="inline-flex items-center gap-1 rounded border border-danger-200 bg-white px-2.5 py-1 text-xs font-medium text-danger-700 hover:bg-danger-50"
                        >
                          <X className="h-3 w-3" /> Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {rejectingTaskId && (
        <RejectTrainingDialog
          manualTrainingId={parseDetails(trainingApprovalTasks.find((t) => t.task_id === rejectingTaskId)!).manual_training_id ?? ''}
          onClose={() => setRejectingTaskId(null)}
          onRejected={() => {
            setRejectingTaskId(null);
            qc.invalidateQueries({ queryKey: ['me', 'tasks', 'open'] });
          }}
        />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-5 py-3 text-sm font-semibold ${active
        ? 'border-accent-600 text-accent-700'
        : 'border-transparent text-ink-secondary hover:text-ink-primary'
        }`}
    >
      {icon}
      {label}
    </button>
  );
}

function RejectTrainingDialog({
  manualTrainingId,
  onClose,
  onRejected,
}: {
  manualTrainingId: string;
  onClose: () => void;
  onRejected: () => void;
}) {
  const [reason, setReason] = useState('');

  const rejectMut = useMutation({
    mutationFn: () => training.manual.reject(manualTrainingId, reason.trim()),
    onSuccess: () => {
      toast.success('Training rejected.');
      onRejected();
    },
    onError: (err) => toast.error(errorText(err, 'Failed to reject training')),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center !m-0 bg-black/60 backdrop-blur-sm p-4">
      <div className="card w-full max-w-sm space-y-4 p-6">
        <div className="flex items-start justify-between">
          <h2 className="text-base font-semibold text-ink-primary">Reject this training</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={rejectMut.isPending}
            className="text-ink-muted hover:text-ink-primary disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div>
          <label className="label text-xs" htmlFor="reject-training-reason">
            Reason *
          </label>
          <textarea
            id="reject-training-reason"
            className="input w-full resize-none"
            rows={3}
            value={reason}
            disabled={rejectMut.isPending}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this training being rejected?"
          />
        </div>
        <div className="flex items-center justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={rejectMut.isPending}>
            Cancel
          </button>
          <button
            type="button"
            className="rounded bg-danger-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-danger-700 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!reason.trim() || rejectMut.isPending}
            onClick={() => rejectMut.mutate()}
          >
            {rejectMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}
